import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, ExternalLink, ShoppingBag } from "lucide-react";
import { api } from "../lib/api";
import BbiShell from "../components/BbiShell";

export default function BrandArticle() {
  const { slug, articleSlug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/public/brands/${slug}/articles/${articleSlug}`).then(({ data }) => {
      setData(data);
      document.title = `${data.article.meta_title || data.article.title} | ${data.brand.name}`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", (data.article.meta_description || data.article.title).slice(0, 155));
      window.scrollTo(0, 0);

      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = "brand-article-jsonld";
      s.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        headline: data.article.title,
        description: data.article.meta_description || "",
        datePublished: data.article.published_at,
        dateModified: data.article.updated_at,
        author: { "@type": "Organization", name: data.brand.name },
        publisher: { "@type": "Organization", name: "EcomCatalog AI", url: "https://ecomcatalog.ai" },
        mainEntityOfPage: `https://ecomcatalog.ai/brands/${slug}/${articleSlug}`,
      });
      document.head.appendChild(s);
    }).catch(() => setErr("Article not found"));
    return () => { document.getElementById("brand-article-jsonld")?.remove(); };
  }, [slug, articleSlug]);

  if (err) return (
    <BbiShell>
      <div className="min-h-[50vh] grid place-items-center text-slate-400 text-sm" data-testid="brand-article-not-found">{err}</div>
    </BbiShell>
  );
  if (!data) return (
    <BbiShell>
      <div className="min-h-[50vh] grid place-items-center text-slate-500 text-sm">Loading article…</div>
    </BbiShell>
  );

  const { article: a, brand: b } = data;
  const shopLinks = [
    b.myntra_url && { label: `Shop ${b.name} on Myntra`, href: b.myntra_url },
    b.meesho_url && { label: `Shop ${b.name} on Meesho`, href: b.meesho_url },
    b.website && { label: `Visit ${b.name}`, href: b.website },
  ].filter(Boolean);

  return (
    <BbiShell>
      <article className="pt-14 sm:pt-20 pb-24">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Link to={`/brands/${slug}`} className="text-xs text-slate-500 hover:text-white flex items-center gap-1.5 mb-8" data-testid="back-to-brand">
            <ArrowLeft className="w-3.5 h-3.5" /> {b.name}
          </Link>

          <div className="flex items-center gap-3 mb-6">
            {b.logo_url ? (
              <img src={b.logo_url} alt={b.name} className="w-10 h-10 rounded-lg object-cover bg-white" />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-fuchsia-500 grid place-items-center text-white font-bold">{b.name[0]}</div>
            )}
            <div>
              <div className="text-sm text-white font-semibold">{b.name}</div>
              <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
                <Calendar className="w-3 h-3" /> {(a.published_at || a.created_at || "").slice(0, 10)}
              </div>
            </div>
          </div>

          <h1 className="font-mkt-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.1]" data-testid="brand-article-title">
            {a.title}
          </h1>

          <div
            data-testid="brand-article-body"
            className="brand-article-body mt-10 text-slate-300 leading-relaxed text-[15px]
              [&_h2]:text-white [&_h2]:font-semibold [&_h2]:text-2xl [&_h2]:mt-10 [&_h2]:mb-4
              [&_h3]:text-white [&_h3]:font-semibold [&_h3]:text-lg [&_h3]:mt-7 [&_h3]:mb-2.5
              [&_p]:mb-4 [&_ul]:mb-5 [&_ul]:pl-5 [&_li]:list-disc [&_li]:mb-1.5
              [&_a]:text-indigo-400 [&_a]:underline [&_a]:underline-offset-2 [&_strong]:text-white"
            dangerouslySetInnerHTML={{ __html: a.body_html }}
          />

          {shopLinks.length > 0 && (
            <div className="mt-12 bg-gradient-to-r from-indigo-500/10 to-fuchsia-500/10 border border-indigo-400/20 rounded-2xl p-6">
              <div className="text-white font-semibold mb-4 flex items-center gap-2"><ShoppingBag className="w-4 h-4 text-indigo-400" /> Where to buy</div>
              <div className="flex gap-3 flex-wrap">
                {shopLinks.map((l) => (
                  <a key={l.href} href={l.href} target="_blank" rel="noopener"
                    className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-indigo-500 hover:bg-indigo-400 text-sm font-semibold transition-colors"
                    style={{ color: "#FFFFFF" }}>
                    {l.label} <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {(data.related || []).length > 0 && (
            <div className="mt-12" data-testid="article-related">
              <div className="text-white font-semibold mb-4">Also read on BrandsByIndia</div>
              <div className="grid sm:grid-cols-2 gap-4">
                {data.related.map((r) => (
                  <Link key={`${r.brand_slug}-${r.slug}`} to={`/brands/${r.brand_slug}/${r.slug}`}
                    className="group bg-white/[0.03] border border-white/10 rounded-xl p-5 hover:border-indigo-400/40 transition-all">
                    <div className="text-[10px] uppercase tracking-wider text-indigo-400 font-bold">{r.brand_name}</div>
                    <div className="text-sm text-white font-semibold mt-1.5 leading-snug line-clamp-2 group-hover:text-indigo-300 transition-colors">{r.title}</div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </BbiShell>
  );
}
